import React, { useEffect, useState } from "react";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { ShowToastMessage } from "../../helper/ShowToastMessage";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1050,
};

const cardStyle = {
  background: "#fff",
  borderRadius: "8px",
  padding: "24px",
  width: "440px",
  maxWidth: "90vw",
  boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
};

const cancelBtn = {
  padding: "6px 16px",
  border: "1px solid #ccc",
  background: "#fff",
  borderRadius: "4px",
  cursor: "pointer",
};

const deleteBtn = {
  padding: "6px 16px",
  border: "none",
  background: "#b00020",
  color: "#fff",
  borderRadius: "4px",
  cursor: "pointer",
};

// userType: "user" (parent) or "organizer".
const DeleteUserModal = ({ userId, userType, displayName, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const label = userType === "organizer" ? "organiser" : "parent";

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        const url = `${process.env.REACT_APP_BASE_URL}/api/admin/user/${userId}/delete-preflight`;
        const res = await serviceConsumer("GET", url);
        if (active) setPreflight(res);
      } catch (err) {
        ShowToastMessage("error", "Failed to check this account");
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [userId]);

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      const url = `${process.env.REACT_APP_BASE_URL}/api/admin/user/${userId}`;
      await serviceConsumer("DELETE", url);
      ShowToastMessage("success", "Account deleted");
      onDeleted();
    } catch (err) {
      const reasons =
        err && err.error && err.error.response && err.error.response.data
          ? err.error.response.data.blockedReasons
          : null;
      if (reasons && reasons.length) {
        setPreflight((p) => ({ ...(p || {}), canDelete: false, blockedReasons: reasons }));
      } else {
        ShowToastMessage("error", (err && err.messages) || "Failed to delete account");
      }
    } finally {
      setDeleting(false);
    }
  };

  const willDelete = (preflight && preflight.willDelete) || {};
  const parts = [];
  if (willDelete.dancers) parts.push(`${willDelete.dancers} dancer${willDelete.dancers === 1 ? "" : "s"}`);
  if (willDelete.registrations) parts.push(`${willDelete.registrations} registration${willDelete.registrations === 1 ? "" : "s"}`);
  if (willDelete.collaborators) parts.push(`${willDelete.collaborators} collaborator${willDelete.collaborators === 1 ? "" : "s"}`);

  const isBlocked = preflight && preflight.canDelete === false;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h4 style={{ marginBottom: "12px" }}>Delete {label} account</h4>

        {loading && <p>Checking account...</p>}

        {!loading && isBlocked && (
          <>
            <p style={{ fontSize: "14px", color: "#444" }}>
              <strong>{displayName}</strong> cannot be deleted because:
            </p>
            <ul style={{ fontSize: "14px", color: "#b00020" }}>
              {(preflight.blockedReasons || []).map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
            <p style={{ fontSize: "13px", color: "#777" }}>
              Resolve these first (refund or cancel entries, or remove events), then try again.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
              <button onClick={onClose} style={cancelBtn}>Close</button>
            </div>
          </>
        )}

        {!loading && !isBlocked && (
          <>
            <p style={{ fontSize: "14px", color: "#444" }}>
              Permanently delete <strong>{displayName}</strong>
              {parts.length ? ` and ${parts.join(", ")}` : ""}. This cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
              <button onClick={onClose} disabled={deleting} style={cancelBtn}>Cancel</button>
              <button onClick={confirmDelete} disabled={deleting} style={deleteBtn}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteUserModal;
